import React, { useEffect, useContext, useState } from 'react';
import { Flexbox, Button } from '@sede-enterprise/shell-ds-react-framework';
import { AppContext } from '../../../../Context/AppContext';
import { Country } from '../../../../Types/types';
import { BiaReportLoader } from './BiaReportLoader/BiaReportLoader';
import { BiaSelectFormField } from './BiaSelectFormField/BiaSelectFormField';
import { fetchFilterInfo } from './BiaUtils';
import { getCombinedMonths } from '../../../Utils/utils'; // Adjust the import path as necessary
import { ASIA_JAPAN } from '../../../../api/constants';
import { IBiaFilterData } from '../../../../Types/types2';

export const BiaReportsFilterSection = () => {
  const { state, dispatch } = useContext(AppContext).biaReports;
  const { state: appDataState } = useContext(AppContext).appData;
  let userRegion = localStorage.getItem('userRegion') ?? appDataState.userRegion;
  const isAsiaUser =
    localStorage.getItem('isAsiaUser') !== null
      ? localStorage.getItem('isAsiaUser') === 'true'
      : appDataState.userRegionFlags.isAsiaUser;
  let filterData = state.biaFilterOptions;

  if (filterData.year) {
    filterData = {
      ...filterData,
      year: filterData.year.map((el) => el.toString()),
    };
  }


  if (filterData.region && isAsiaUser) {
    filterData = {
      ...filterData,
      region: filterData.region.filter((region: string) =>
        region.startsWith('ASIA-')
      ),
    };
  }

  // Define local state for each filter field
  const [yearSelected, setYearSelected] = useState<string[]>([]);
  const [quarterSelected, setQuarterSelected] = useState<string[]>([]);
  const [monthSelected, setMonthSelected] = useState<string[]>([]);
  const [regionSelected, setRegionSelected] = useState<string>(
    (userRegion || appDataState.userRegion || filterData.region[0])
  );
  const [countrySelected, setCountrySelected] = useState<string[]>([]);
  const [countryCodeSelected, setCountryCodeSelected] = useState<string[]>([]);
  const [commoditySelected, setCommoditySelected] = useState<string[]>([]);
  const [unitNameSelected, setUnitNameSelected] = useState('TWH');
  const [countryOptions, setCountryOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (isAsiaUser) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      userRegion = ASIA_JAPAN;
    }
    fetchFilterInfo(dispatch, setYearSelected, handleRegionChange, userRegion);
  }, []);

  useEffect(() => {
    setRegionSelected(
      (userRegion ?? filterData.region[0])
    );
  }, [userRegion]);

  useEffect(() => {
    setRegionSelected(state.biaFilterData.biaRegion.toUpperCase());
  }, [state.biaFilterData.biaRegion]);

  if (!filterData) {
    return null; // or a loading spinner
  }

  const handleApplyFilters = () => {
    const updatedRegionSelected = isAsiaUser && !regionSelected?.length ? ASIA_JAPAN : regionSelected;
    const currentYear = new Date().getFullYear();
    if (yearSelected.length === 0) {
      setYearSelected([currentYear.toString()]);
    }
    const payload: IBiaFilterData = {
      biaRegion: updatedRegionSelected,
      biaYear: yearSelected ?? currentYear,
      biaQuarter: quarterSelected,
      biaMonth: monthSelected,
      biaCountry: countrySelected,
      biaCountryCode: countryCodeSelected,
      biaCommodity: commoditySelected,
      biaUnitName: unitNameSelected ?? '',
    };

    dispatch({
      type: 'setBiaFilterData',
      payload,
    });
  };

  const handleClearFilters = () => {
    const updatedUserRegion = isAsiaUser ? ASIA_JAPAN : userRegion;
    const currentYear = new Date().getFullYear().toString();
    const defaultRegion = filterData.region[0];
    // Reset selections
    setCommoditySelected([]);
    setUnitNameSelected('TWH'); // This is common to both branches, so we set it outside
    // Reset local state
    setYearSelected([]);
    setQuarterSelected([]);
    setMonthSelected([]);
    setCountrySelected([]);
    setCountryCodeSelected([]);
    const payload: IBiaFilterData = {
      biaRegion: appDataState.isNcfReportingRegionFieldDisabled
        ? updatedUserRegion
        : defaultRegion,
      biaYear: [currentYear],
      biaQuarter: [],
      biaMonth: [],
      biaCountry: [],
      biaCountryCode: [],
      biaCommodity: [],
      biaUnitName: 'TWH',
    };

    // Update local state for BIA report
    setYearSelected([currentYear]);
    if (!appDataState.isNcfReportingRegionFieldDisabled) {
      setRegionSelected(defaultRegion);
    }

    // Dispatch action with the constructed payload
    dispatch({
      type: 'setBiaFilterData',
      payload,
    });
  };

  const handleRegionChange = (e: string, regionCountryMapping?: Record<string, Country[]>) => {
    // For isBiaReportPage, e is expected to be a string.
    // Directly use e as it is.
    setRegionSelected(e);

    setCountrySelected([]);
    setCountryCodeSelected([]);
    let cntryOptions: React.SetStateAction<
      {
        label: string;
        value: string;
      }[]
    > = [];
    const countryFieldOptions = regionCountryMapping ?? filterData.regionCountryMapping;

    if (countryFieldOptions[e]) {
      cntryOptions = countryFieldOptions[e].map((el) => {
        return {
          label: el.COUNTRY_NAME,
          value: el.COUNTRY_CODE,
        };
      });
    } else {
      cntryOptions = [
        {
          label: '',
          value: '',
        },
      ];
    }
    setCountryOptions(cntryOptions);
  };

  return (
    <div data-testid='filter' style={{ padding: '20px', width: '25%' }}>
      <Flexbox flexDirection='column'>
        <div
          style={{
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '17px',
            lineHeight: '26px',
            color: '#404040',
            padding: '10px 0',
            marginTop: '26px',
          }}
        >
          Filters
        </div>
        {state.isBiaFilterOptionsLoading ? (
          <div
            style={{
              border: '1px solid #ccc',
              padding: '10px',
              borderRadius: '4px',
            }}
          >
            <BiaReportLoader loadingText='filter options' />
          </div>
        ) : (
          <>
            <BiaSelectFormField
              id='year'
              label='Year'
              testId='Year'
              value={yearSelected}
              onChange={setYearSelected}
              options={filterData.year.map((el) => el.toString())}
              mode='multiple'
            />
            {yearSelected.length === 1 && (
              <BiaSelectFormField
                id='quarter'
                label='Quarter'
                testId='Quarter'
                value={quarterSelected}
                onChange={setQuarterSelected}
                options={filterData.quarter}
                mode='multiple'
              />
            )}
            {quarterSelected.length > 0 && (
              <BiaSelectFormField
                id='month'
                label='Month'
                testId='Month'
                value={monthSelected}
                onChange={setMonthSelected}
                options={getCombinedMonths(quarterSelected)}
                mode='multiple'
              />
            )}
            <BiaSelectFormField
              id='region'
              label='Region'
              testId='Region'
              value={regionSelected}
              onChange={(e: string) => handleRegionChange(e)}
              options={filterData.region.map((el) => ({
                label: el,
                value: el,
              }))}
              mode={'single'}
              disabled={appDataState.isNcfReportingRegionFieldDisabled}
            />
            <BiaSelectFormField
              id='country'
              label='Country'
              testId='Country'
              value={countrySelected}
              onChange={setCountrySelected}
              options={countryOptions}
              mode='multiple'
            />
            <BiaSelectFormField
              id='commodity'
              label='Commodity'
              testId='Commodity'
              value={commoditySelected}
              onChange={setCommoditySelected}
              mode='multiple'
              options={filterData.commodity.map((el) => ({
                label: el.TRADE_NAME,
                value: el.TRADE_CODE,
              }))}
            />
            <BiaSelectFormField
              id='unitName'
              label='Unit Name'
              testId='Unit Name'
              value={unitNameSelected}
              onChange={setUnitNameSelected}
              options={filterData.unitName}
            />
            <Button className='getDataBtn' data-testid='Apply Filters' onClick={handleApplyFilters}>
              Apply Filters
            </Button>
            <Button
              className='getDataBtn'
              variant='outlined'
              style={{ marginTop: '10px' }}
              data-testid='Clear Filters'
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </>
        )}
      </Flexbox>
      <div style={{ borderLeft: '1px solid rgba(0, 0, 0, 0.06)' }}></div>
    </div>
  );
};
